import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { format, isSameDay } from "date-fns";

import {
	faCalendar,
	faPause,
	faPaperPlane,
	faPen,
	faPlay,
	faTrash,
	faX,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list";

import { resolveScheduledFor } from "../utils/resolveScheduledFor.util";
import {
	isScheduleCancellable,
	isScheduleEditable,
	isSchedulePausable,
	isSchedulePaused,
	isScheduleSendableNow,
	isScheduleWaitingForInvite,
	resolveSchedule,
} from "../utils/resolveSchedule.util";
import { useCancelScheduleModal } from "../utils/useCancelScheduleModal";
import { useEditScheduleModal } from "../utils/useEditScheduleModal";
import { usePauseScheduleModal } from "../utils/usePauseScheduleModal";
import { useSendNowScheduleModal } from "../utils/useSendNowScheduleModal";

const formatSendTime = (iso: string): string =>
	format(new Date(iso), isSameDay(new Date(iso), new Date()) ? "HH:mm" : "HH:mm dd.MM.yyyy");

/**
 * The status line under a scheduled bubble and the controls on it: when the message goes out,
 * and — for a message that is still pending — Cancel, plus Pause / Resume, Edit and Send now on a
 * manual follow-up (PREC-2861, PREC-4220, PREC-3103). Every flow is self-contained here, modal and snackbar
 * included, so the thread's host has nothing to thread through.
 *
 * Every control is decided from the schedule row itself (`resolveSchedule.util`), so a button is
 * only rendered where the API would accept the request: a campaign step shows no Pause, and a
 * cancelled or already-sent row shows nothing at all. A follow-up parked behind a LinkedIn
 * invitation has no send time yet; its line says when it will go out instead.
 */
export const ScheduledMessageControls: FunctionComponent<{
	conversation: Conversation;
	schedules?: ListPersonSchedule[];
	/** The extension renders this subtree in a shadow root; portalled popups target this. */
	portalContainer?: HTMLElement | null;
}> = ({ conversation, schedules, portalContainer }) => {
	const { t } = useTranslation();
	const { requestPauseOrResume, renderPauseScheduleModal } = usePauseScheduleModal();
	const { requestCancelSchedule, renderCancelScheduleModal } = useCancelScheduleModal();
	const { requestEdit, renderEditScheduleModal } = useEditScheduleModal({ portalContainer });
	const { requestSendNow, renderSendNowScheduleModal } = useSendNowScheduleModal();
	const scheduledFor = resolveScheduledFor(conversation, schedules);
	const schedule = resolveSchedule(conversation, schedules);

	if (schedule?.canceledAt) {
		return (
			<div className="text-xs text-status-error font-normal flex items-center gap-1">
				<Icon icon={faX} />
				{t("outbox.cancelSchedule.cancelled")}
			</div>
		);
	}

	const paused = isSchedulePaused(schedule);
	const pausable = isSchedulePausable(schedule);
	const waitingForInvite = isScheduleWaitingForInvite(schedule);

	const statusText = (): string => {
		if (waitingForInvite) {
			const count = schedule?.scheduledInDays ?? 0;

			if (paused) {
				return t("outbox.pauseSchedule.pausedWaitingForInvite", { count });
			}

			return count > 0 ? t("outbox.waitingForInvite", { count }) : t("outbox.waitingForInviteNow");
		}

		if (paused) {
			return t(
				scheduledFor ? "outbox.pauseSchedule.pausedWithTime" : "outbox.pauseSchedule.paused",
				{ time: scheduledFor ? formatSendTime(scheduledFor) : "" }
			);
		}

		return scheduledFor
			? t("outbox.scheduledFor", { time: formatSendTime(scheduledFor) })
			: t("outbox.scheduled");
	};

	return (
		<div className="text-xs text-gray-700 font-normal flex items-center gap-2 flex-wrap">
			<div className="flex items-center gap-1" data-testid="scheduled-message-status">
				<Icon icon={paused ? faPause : faCalendar} />
				{statusText()}
			</div>
			{schedule && (pausable || paused) && (
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={paused ? faPlay : faPause} />}
					size={ButtonSize.XS}
					title={t(
						paused ? "outbox.pauseSchedule.resumeAction" : "outbox.pauseSchedule.pauseAction"
					)}
					onClick={() => requestPauseOrResume(schedule)}
				/>
			)}
			{schedule && isScheduleSendableNow(schedule) && (
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={faPaperPlane} />}
					size={ButtonSize.XS}
					title={t("outbox.sendNowSchedule.action")}
					onClick={() =>
						requestSendNow(schedule, conversation.type?.startsWith("mail") ? "email" : "linkedin")
					}
				/>
			)}
			{schedule && isScheduleEditable(schedule) && (
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={faPen} />}
					size={ButtonSize.XS}
					title={t("outbox.editSchedule.action")}
					onClick={() => requestEdit(schedule, conversation)}
				/>
			)}
			{schedule && isScheduleCancellable(schedule) && (
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={faTrash} />}
					size={ButtonSize.XS}
					title={t("outbox.cancelSchedule.action")}
					onClick={() => requestCancelSchedule(schedule.id)}
				/>
			)}
			{renderPauseScheduleModal()}
			{renderCancelScheduleModal()}
			{renderEditScheduleModal()}
			{renderSendNowScheduleModal()}
		</div>
	);
};
